/**
 * Loading Screen with Personality
 * Fun, engaging loading states with rotating messages
 */

import { useState, useEffect } from "react";
import { motion, AnimatePresence, type Variants } from "framer-motion";
import { getLoadingMessage, type LoadingContext } from "../../lib/personality";

interface LoadingWithPersonalityProps {
  /** Context for appropriate messages */
  context?: LoadingContext;
  /** Custom messages to override defaults */
  messages?: string[];
  /** How often to rotate messages (ms) */
  rotateInterval?: number;
  /** Size variant */
  size?: "sm" | "md" | "lg";
  /** Show spinner */
  showSpinner?: boolean;
  /** Custom icon/emoji */
  icon?: string;
  /** Additional className */
  className?: string;
}

const sizeClasses = {
  sm: { container: "py-4", text: "text-sm", spinner: "w-5 h-5", icon: "text-2xl" },
  md: { container: "py-8", text: "text-base", spinner: "w-8 h-8", icon: "text-4xl" },
  lg: { container: "py-12", text: "text-lg", spinner: "w-10 h-10", icon: "text-5xl" },
};

const spinnerVariants: Variants = {
  spin: {
    rotate: 360,
    transition: {
      duration: 1,
      repeat: Infinity,
      ease: "linear" as const,
    },
  },
};

const messageVariants = {
  enter: { opacity: 0, y: 10 },
  center: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 },
};

const iconPulse: Variants = {
  animate: {
    scale: [1, 1.1, 1],
    transition: {
      duration: 1.5,
      repeat: Infinity,
      ease: "easeInOut",
    },
  },
};

export function LoadingWithPersonality({
  context = "default",
  messages,
  rotateInterval = 3000,
  size = "md",
  showSpinner = true,
  icon,
  className = "",
}: LoadingWithPersonalityProps) {
  const [currentMessage, setCurrentMessage] = useState(() =>
    messages?.[0] || getLoadingMessage(context)
  );
  const [messageIndex, setMessageIndex] = useState(0);

  useEffect(() => {
    const customMessages = messages || [];

    const interval = setInterval(() => {
      if (customMessages.length > 0) {
        const next = (messageIndex + 1) % customMessages.length;
        setCurrentMessage(customMessages[next]);
        setMessageIndex(next);
      } else {
        setCurrentMessage(getLoadingMessage(context));
      }
    }, rotateInterval);

    return () => clearInterval(interval);
  }, [context, messages, rotateInterval, messageIndex]);

  const sizes = sizeClasses[size];

  return (
    <div className={`flex flex-col items-center justify-center ${sizes.container} ${className}`}>
      {/* Icon or Spinner */}
      <div className="mb-4">
        {icon ? (
          <motion.div
            variants={iconPulse}
            animate="animate"
            className={sizes.icon}
          >
            {icon}
          </motion.div>
        ) : showSpinner ? (
          <motion.div
            variants={spinnerVariants}
            animate="spin"
            className={`${sizes.spinner} border-2 border-gray-600 border-t-purple-500 rounded-full`}
          />
        ) : null}
      </div>

      {/* Rotating message */}
      <div className={`h-6 ${sizes.text} text-gray-400`}>
        <AnimatePresence mode="wait">
          <motion.p
            key={currentMessage}
            variants={messageVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3 }}
          >
            {currentMessage}
          </motion.p>
        </AnimatePresence>
      </div>

      {/* Animated dots */}
      <div className="flex gap-1 mt-3">
        {[0, 1, 2].map((i) => (
          <motion.span
            key={i}
            className="w-1.5 h-1.5 bg-purple-500 rounded-full"
            animate={{ y: [0, -4, 0] }}
            transition={{
              duration: 0.6,
              repeat: Infinity,
              delay: i * 0.15,
            }}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Full-page loading overlay
 */
export function LoadingOverlay({
  isVisible,
  context = "default",
  message,
}: {
  isVisible: boolean;
  context?: LoadingContext;
  message?: string;
}) {
  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/80 backdrop-blur-sm"
        >
          <LoadingWithPersonality
            context={context}
            messages={message ? [message] : undefined}
            size="lg"
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * Inline loading indicator with message
 */
export function InlineLoading({
  message,
  className = "",
}: {
  message?: string;
  className?: string;
}) {
  return (
    <div className={`flex items-center gap-2 text-gray-400 ${className}`}>
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        className="w-4 h-4 border-2 border-gray-600 border-t-purple-500 rounded-full"
      />
      <span className="text-sm">{message || getLoadingMessage("default")}</span>
    </div>
  );
}

/**
 * Card dealing loading animation
 */
export function DealingCards({ cardCount = 5 }: { cardCount?: number }) {
  return (
    <div className="flex justify-center items-end gap-2 py-8">
      {Array.from({ length: cardCount }).map((_, i) => (
        <motion.div
          key={i}
          initial={{ y: -100, opacity: 0, rotate: -20 }}
          animate={{ y: 0, opacity: 1, rotate: (i - Math.floor(cardCount / 2)) * 5 }}
          transition={{
            delay: i * 0.1,
            type: "spring",
            stiffness: 200,
            damping: 15,
          }}
          className="w-12 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg shadow-lg"
        />
      ))}
    </div>
  );
}
